import { Key, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Dropdown, DropdownItem, XIcon } from "staysure-component-library";

import ButtonWrapper, {
  IconButtonWrapper,
} from "@/app/shared-components/button-wrapper/button-wrapper";
import ModalWrapper from "@/app/shared-components/modal/modal-wrapper";
import RichText from "@/components/atoms/rich-text/rich-text";
import { RootState } from "@/store/root-reducer";
import { updateCountryChangeModal } from "@/store/slices/country-change.slice";

import { CountryChangeDropDownItem } from "./country-change-drop-down-item";
import classes from "./country-change-modal.module.css";
import styles from "./drop-down.module.css";
import { useCountryChangeFooter } from "../country-change-footer/change-country-footer.hook";
import { NormalizedCountryDetail } from "./types/country-change-modal-types";

function CountryChangeModal() {
  const dispatch = useDispatch();
  const [selectedCountry, setSelectedCountry] =
    useState<NormalizedCountryDetail | null>(null);

  const { modalOpen } = useSelector(
    (state: RootState) => state.countryChangeModal
  );

  const closeModal = () => {
    dispatch(
      updateCountryChangeModal({
        modalOpen: false,
      })
    );
  };

  const { data, defaultCountry, normalizedCountries } =
    useCountryChangeFooter();

  useEffect(() => {
    if (!modalOpen) {
      setSelectedCountry(null);
    }
  }, [modalOpen]);

  const isCountryChanged = useMemo(() => {
    return (
      selectedCountry?.countryText &&
      selectedCountry?.countryText !== defaultCountry?.countryText
    );
  }, [selectedCountry?.countryText, defaultCountry?.countryText]);

  const handleSelectionChange = (key: Key | null) => {
    if (key === null) {
      setSelectedCountry(defaultCountry);
      return;
    }
    const value = String(key);
    const country = normalizedCountries.find((c) => c.countryText === value);
    setSelectedCountry(country ?? defaultCountry);
  };

  return (
    <ModalWrapper
      className=" md:p-spacing-xxxl p-spacing-l md:max-w-[416px] max-w-[327px] w-full  "
      isOpen={modalOpen}
      setIsOpen={closeModal}
    >
      <div data-testid="country-change-modal">
        <h1
          className="
        text-text-title font-header-font md:text-3xl text-[23px] sm:mt-spacing-m md:mt-0
        md:leading-[34px] leading-[27.6px] !mb-spacing-xs md:!mb-spacing-s
      "
        >
          {data?.modalTitle}
        </h1>
      </div>
      <div>
        <RichText
          className="text-text-body font-body-font text-[18px] leading-[27px]  md:mb-spacing-l mb-spacing-xs [&_p+p]:!mt-[12px]"
          richText={data?.modalDescription ?? ""}
        />
      </div>
      <div
        className={`${styles.country_wrapper}
           md:w-[304px] w-[279px] mx-auto 
            md:h-[56px] mt-spacing-l md:mb-spacing-l mb-spacing-l
          `}
      >
        <Dropdown
          id="country-change-modal-dropdown"
          className={`${classes.dropdown_btn} py-spacing-m py px-spacing-m flex w-full !justify-start !items-start `}
          strokeColor="#272427"
          onSelectionChange={handleSelectionChange}
          placeholderTextStyle={{ color: "#4F4F4F", fontWeight: 600 }}
          listBoxStyle={{
            outline: "none",
            margin: "0px 0px 0px 0px",
            minWidth: "unset !important",
            boxShadow: "0px 0px 12px 0px rgba(15, 23, 42, 0.11)",
          }}
          defaultValue={defaultCountry?.countryText}
          value={selectedCountry?.countryText}
          listClassName={`${classes.listStyle} w-full gap-spacing-xxs rounded text-text-on-primary`}
          unorderedListClassName="md:w-[304px] w-[279px]  text-[16px] leading-[24px]  !border-border-subdued !rounded-radius-lg !px-spacing-m !py-spacing-m  !justify-start !items-start  !md:ml-0 !ml-[-1px] !mt-[-4px]"
        >
          {normalizedCountries.map((country) => (
            <DropdownItem value={country.countryText} key={country.countryText}>
              <CountryChangeDropDownItem
                countryFlag={country.countryFlag}
                country={country.countryText}
                language={country.countryLanguage}
              />
            </DropdownItem>
          ))}
        </Dropdown>
      </div>
      <div className="flex flex-col w-full items-center gap-spacing-xs">
        <ButtonWrapper
          id="country-change-modal-continue-button"
          isDisabled={!isCountryChanged}
          hierarchy="primary"
          label={data?.modalConfirmationBtnText ?? ""}
          onClick={() => {
            window.location.href = selectedCountry?.redirectUrl ?? "";
          }}
          isClickableDisabled={!isCountryChanged}
          className="
          md:mr-1 md:mb-0 !h-[48px]
          leading-[18px] text-text-on-primary
        "
        />
        <ButtonWrapper
          id="-modal-cancel-button"
          hierarchy="secondary"
          label={data?.modalCancelBtnText ?? ""}
          onClick={closeModal}
          className="
          !h-[48px]  leading-[18px]
        "
        />
        <IconButtonWrapper
          data-testid="modal-close-button"
          id="-modal-close-button"
          hierarchy="tertiary"
          btnType="icon"
          className={`!absolute md:right-[12px] md:top-[8px]  right-spacing-xxs top-spacing-xxs md:bg-surface-neutral-option-1
                hover:!bg-surface-neutral-option-1 active:!bg-surface-neutral-option-1
              bg-transparent md:w-[24px] md:h-[24px] flex items-start justify-end !rounded-bl-radius-lg !rounded-[0px] `}
          icon={<XIcon stroke="#306582 " />}
          onClick={closeModal}
        />
      </div>
    </ModalWrapper>
  );
}

export default CountryChangeModal;
